import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, parse } from "date-fns"
import { toZonedTime } from "date-fns-tz"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const formatIntegerBrlCurrency = (
  value: number | string | undefined
) => {
  if (!value) {
    return "R$ 0,00"
  }
  return (Number(value) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  })
}

export function formatDocument(document: string): string {
  if (!document) {
    return "Não informado"
  }
  // Remove only formatting separators, preserve letters for alphanumeric CNPJ support.
  const sanitized = document
    .replace(/[.\-/\s]/g, "")
    .toUpperCase()

  if (sanitized.length === 11) {
    return sanitized.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      "$1.$2.$3-$4"
    )
  } else if (sanitized.length === 14) {
    // Position-based slicing supports both numeric and alphanumeric CNPJ (XX.XXX.XXX/XXXX-XX)
    return `${sanitized.slice(0, 2)}.${sanitized.slice(2, 5)}.${sanitized.slice(5, 8)}/${sanitized.slice(8, 12)}-${sanitized.slice(12, 14)}`
  }

  return document
}

export async function fetchCepData(cep: string) {
  try {
    const response = await fetch(
      `https://viacep.com.br/ws/${cep}/json/`
    )
    const data = await response.json()

    if (data.erro) {
      throw new Error("CEP inválido")
    }

    return {
      street: data.logradouro,
      city: data.localidade,
      state: data.uf,
      neighborhood: data.bairro,
    }
  } catch (error) {
    console.error("Erro ao buscar CEP:", error)
    throw error
  }
}

interface FormatDateOptions {
  date: string | Date | undefined
  pattern?: string
  preserveTimezone?: boolean
}

export function formatDate({
  date,
  pattern = "dd/MM/yyyy",
  preserveTimezone = false,
}: FormatDateOptions): string {
  if (!date) return "Não informado"

  try {
    let dateObject: Date

    if (typeof date === "string") {
      // Handle ISO date string with time (e.g. "2025-02-07T12:04:59.000Z")
      if (date.includes("T")) {
        dateObject = new Date(date)
      }
      // Handle simple date string (e.g. "2025-01-15")
      else {
        dateObject = parse(date, "yyyy-MM-dd", new Date())
      }
    } else {
      dateObject = date
    }

    if (isNaN(dateObject.getTime())) {
      throw new Error("Invalid date")
    }

    if (
      preserveTimezone &&
      typeof date === "string" &&
      date.endsWith("Z")
    ) {
      // Use UTC timezone for formatting
      const utcDate = toZonedTime(dateObject, "UTC")
      return format(utcDate, pattern)
    }

    return format(dateObject, pattern)
  } catch (error) {
    console.error("Error formatting date:", error)
    return "Data inválida"
  }
}
